import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { initErrorReporter } from './utils/errorReporter';
import { getStoredTheme, applyTheme } from './utils/theme';
import { rkLog } from './utils/debug';
import { installPreloadErrorHandler } from './utils/preloadErrorReload';
import { serveTokenToServiceWorker } from '@quig/cxq-pwa-kit';
import { getAuth, tokenExpiresSoon, refreshSession } from './utils/auth';
import { startAutoUpdate } from '@quig/cxq-pwa-kit';

// Apply stored theme before first paint to avoid flash
applyTheme(getStoredTheme());

// Start client-side error reporting before anything else
initErrorReporter();

// Recover from dynamic-import version-skew (stale tab importing a chunk the
// dist rebuild deleted — e.g. the schedule Print button). See module comment.
installPreloadErrorHandler();

// Answer the service worker's token requests.
//
// THE SERVICE WORKER HAS BEEN ASKING SINCE THIS CODE SHIPPED AND NOTHING EVER
// ANSWERED. service-worker.ts posts {type:'GET_TOKEN'} to each window with a
// 500ms timeout; a repo-wide search for a navigator.serviceWorker message
// listener found only WebSocket handlers. So authToken was always null and
// syncWellnessReports() returned early on every invocation -- queued disaster
// reports have never synced, silently, since the feature was written.
//
// Registered here rather than in a component so it is alive for the whole page
// lifetime: a background sync can fire while the user sits on any route, and a
// responder mounted under a route would be absent exactly when it is needed.
//
// The token is read per request (not captured once) and refreshed first if it
// is close to expiry, so the SW never receives a token that expires mid-sync.
// refreshSession() de-duplicates concurrent calls internally, so a burst of
// requests cannot stampede the refresh endpoint.
serveTokenToServiceWorker(async () => {
  if (tokenExpiresSoon()) await refreshSession();
  return getAuth()?.token ?? null;
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// ---------------------------------------------------------------------------
// Automatic service-worker updates — delegated to @quig/cxq-pwa-kit.
//
// WHAT THIS REPLACES, and why it had to be replaced rather than repaired:
// this file previously carried ~113 lines of its own update ladder — SW
// registration, a /api/version poll, a SKIP_WAITING handshake, a hard-reload
// fallback and a cache-nuke last resort, commented "infallible automatic
// cache busting". It pivoted on one comparison:
//
//     if (!data.version || data.version === __APP_VERSION__) return;
//
// __APP_VERSION__ is compiled from frontend/package.json, and /api/version
// returns THAT SAME package.json version. Nothing bumps it on deploy, so both
// sides read the identical string on every poll — measured live at 0.4.431
// while five separate bundles shipped under it in one day. The comparison
// returned early every single time and the ladder below it never ran once.
// A toNum() guard underneath refused to reload into anything not strictly
// newer, and by its own measure nothing was ever newer.
//
// The code was correct. The SIGNAL was the defect: it compared a
// hand-maintained string that does not change on deploy against itself.
//
// The library triggers on the browser's own service-worker byte-comparison
// instead, which needs no version string, no server endpoint, and no human
// step — it cannot silently agree with itself the way the old check did.
// It also handles a case this file never did: checking registration.waiting
// DIRECTLY on resume (visibility/focus/pageshow), because updatefound can
// fire before the listener attaches when a frozen PWA context is restored.
//
// Same class of bug as PR #734, which fixed PWA staleness caused by
// service-worker.js itself being CDN-cached. Both were update paths that were
// present, plausible and inert — which is the failure mode to watch for here,
// not a loud error.
//
// No options are passed except the log sink: the library's defaults (2 min
// poll, 3 reloads bounded per target, sessionStorage-backed bound) are
// deliberate and documented in its own source. RK previously polled at 60s;
// that difference is not worth configuring.
// ---------------------------------------------------------------------------
startAutoUpdate({
  log: (level, msg, extra) => rkLog(`[RK][pwa-kit][${level}] ${msg}`, extra),
});
